import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { cn } from '~/lib/cn';
import { FlexCol } from '~/modules/artist-roster/components/flex/FlexCol';
import { FlexRow } from '~/modules/artist-roster/components/flex/FlexRow';
import { DefaultQuestions } from '~/modules/music-iq/components/DefaultQuestions';
import { MusicIqTitle } from '~/modules/music-iq/pages/chatbot-page/components/MusicIqTitle';

import { ChatPrompter } from './components/ChatPrompter/ChatPrompter';
import { Thread } from './components/Thread';
import { useMusicIqAgent } from './hooks/useMusicIqAgent';

export function ChatbotPage() {
  const { t } = useTranslation('data');

  const { messages, ask, abort, isThinking, isProcessingQuestion } =
    useMusicIqAgent();

  const [hasAskedAQuestion, setHasAskedAQuestion] = useState(false);

  const askQuestion = useCallback(
    async (question: string) => {
      setHasAskedAQuestion(true);
      await ask(question);
    },
    [ask],
  );

  const handleTitleClick = useCallback(() => {
    abort();
    setHasAskedAQuestion(false);
  }, [abort]);

  return (
    <FlexCol
      id="chatbot-page"
      className="h-full overflow-y-auto bg-[linear-gradient(175deg,#2B2D1B_0%,#121C2D_41%)]"
    >
      <Helmet>
        <title>{t('musicIq.pageTitle')}</title>
      </Helmet>

      {hasAskedAQuestion ? (
        <FlexRow className="m-xl">
          <MusicIqTitle
            iconClassName="size-[25px]"
            titleClassName="text-body-md"
            onClick={handleTitleClick}
          />
        </FlexRow>
      ) : null}

      <FlexCol
        className={cn(
          'mx-auto w-[700px] grow items-center',
          !hasAskedAQuestion && 'justify-center',
        )}
      >
        {!hasAskedAQuestion ? (
          <DefaultQuestions onQuestionSelected={askQuestion} />
        ) : (
          <Thread messages={messages} isThinking={isThinking} />
        )}
      </FlexCol>

      <div
        className="sticky bottom-0 w-full pb-xl pt-md"
        style={{ backgroundColor: '#121C2D' }}
      >
        <ChatPrompter
          isProcessingQuestion={isProcessingQuestion}
          onAskQuestion={askQuestion}
          onStop={abort}
          className="mx-auto w-[700px]"
        />
      </div>
    </FlexCol>
  );
}

ChatbotPage.displayName = 'ChatbotPage';
